import { useTranslation } from "../../context/TranslationContext";
import ProgressBar from "../ds/ProgressBar";
import StatCell from "../ds/StatCell";
import { formatCampaignMoney } from "../../utils/campaignCardUtils";

const money = (value) => `${formatCampaignMoney(value || 0)} ₾`;

/** Figma O3, for one campaign: budget, reach and where submissions stand. */
const CampaignAnalytics = ({ campaign, submissions }) => {
  const { t } = useTranslation();
  const budget = campaign.budget || 0;
  const spent = campaign.spent || 0;
  const used = budget > 0 ? Math.min(spent / budget, 1) : 0;
  const approved = submissions.filter((row) => row.status === "approved");
  const views = approved.reduce((sum, row) => sum + (row.viewCount || 0), 0);
  const creators = new Set(submissions.map((row) => String(row.submittedBy?._id || row.submittedBy))).size;
  const rate = submissions.length ? Math.round((approved.length / submissions.length) * 100) : 0;
  const count = (status) => submissions.filter((row) => row.status === status).length;

  return (
    <div className="flex flex-col gap-space-24">
      <section className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
        <div className="flex flex-wrap items-center justify-between gap-space-8">
          <h2 className="text-title-3 text-label-primary">{t("campaignFlow.budgetSpend")}</h2>
          <p className="text-subheadline-semibold text-app-creator-rewards-soft">
            {t("campaignFlow.budgetUsed", { percent: Math.round(used * 100) })}
          </p>
        </div>
        <p className="text-footnote text-label-secondary">{t("campaignFlow.budgetHint")}</p>
        <ProgressBar value={used} />
        <div className="mt-space-4 grid grid-cols-2 gap-space-12 min-[900px]:grid-cols-4 min-[900px]:gap-space-24">
          <StatCell label={t("campaignFlow.totalBudget")} value={money(budget)} />
          <StatCell label={t("mintSpace.spent")} value={money(spent)} />
          <StatCell label={t("campaignFlow.remaining")} value={money(Math.max(budget - spent, 0))} />
          <StatCell label={t("campaignFlow.costPerThousand")} value={views > 0 ? `${((spent / views) * 1000).toFixed(2)} ₾` : "—"} />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-space-12 min-[600px]:grid-cols-3 min-[600px]:gap-space-24">
        <StatCell label={t("campaignFlow.views")} value={views.toLocaleString()} meta={t("campaignFlow.viewsMeta")} />
        <StatCell label={t("campaignFlow.creators")} value={creators} meta={t("campaignFlow.creatorsMeta")} />
        <StatCell
          label={t("campaignFlow.approvalRate")}
          value={`${rate}%`}
          meta={t("campaignFlow.approvalMeta", { approved: approved.length, total: submissions.length })}
        />
      </section>

      <section className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
        <div className="flex flex-col gap-space-4">
          <h2 className="text-headline text-label-primary">{t("campaignFlow.pipeline")}</h2>
          <p className="text-footnote text-label-secondary">{t("campaignFlow.pipelineHint")}</p>
        </div>
        <div className="grid grid-cols-1 gap-space-12 min-[600px]:grid-cols-3 min-[600px]:gap-space-24">
          <StatCell label={t("campaignFlow.status_pending")} value={count("pending")} />
          <StatCell label={t("campaignFlow.status_approved")} value={count("approved")} />
          <StatCell label={t("campaignFlow.status_rejected")} value={count("rejected")} />
        </div>
      </section>
    </div>
  );
};

export default CampaignAnalytics;
